import type { BoxProps, HTMLChakraProps } from '@chakra-ui/react';
import {
  Box,
  Flex,
  Float,
  Highlight,
  HStack,
  Image,
  Link,
  LinkOverlay,
  Progress,
  Text,
  useSlotRecipe,
} from '@chakra-ui/react';
import { ChainLayer, NeverError } from '@cronos-app/sdk';
import type { IntlInternalUrl } from '@ts-intl/integration';
import { Address } from '@ui/components/Address';
import { Avatar } from '@ui/components/avatar';
import { MaskIcon } from '@ui/components/MaskIcon';
import { InDesktop, InMobile } from '@ui/components/MobileOrDesktop';
import MobileSupportTooltip from '@ui/components/MobileSupportTooltip';
import { Num } from '@ui/components/Num';
import { PercentChange } from '@ui/components/Num/PercentChange';
import { UsdValue } from '@ui/components/Num/UsdValue';
import { EXTERNAL_LINK } from '@ui/config/externalLinks';
import { DEX_NAME_SHORT } from '@ui/config/layer';
import { paths } from '@ui/config/paths';
import type { Agent } from '@ui/hooks/api/useAgents';
import { useGlobalChainLayer } from '@ui/hooks/useGlobalChainLayer';
import { NextLinkWithIntl } from '@ui/locales/browserSSG';
import { checkHoursPassed } from '@ui/utils';
import { formatDistanceToNow } from 'date-fns';
import { memo, type ReactNode, useMemo } from 'react';

import { useAgentsView } from './useAgentsView';

type AgentCardProps = BoxProps & {
  agent: Agent;
  variant?: 'special';
};
export const AgentCard = memo(
  ({ agent, variant, ...props }: AgentCardProps) => {
    const chainLayer = useGlobalChainLayer();
    const recipe = useSlotRecipe({ key: 'agentCard' });
    const styles = recipe({ variant });
    const createdAtNode = useMemo(() => {
      return agent.created_at
        ? formatDistanceToNow(agent.created_at, {
            addSuffix: true,
          })
        : '';
    }, [agent.created_at]);

    const creatorNode = useMemo(() => {
      return agent.creator_address ? (
        <Address as="span" display="inline" address={agent.creator_address} />
      ) : (
        '--'
      );
    }, [agent.creator_address]);

    const createTextNode = (
      <Text
        textStyle="caption"
        color="text.secondary"
        textAlign="right"
        lineClamp={1}
        wordBreak="break-all"
      >
        Created {createdAtNode} by {creatorNode}
      </Text>
    );

    const tradeOnPlatformStr = useMemo(() => {
      if (chainLayer === ChainLayer.L2) return 'Trade on H2';
      if (chainLayer === ChainLayer.CronosEvm) return 'Trade on VVS';
      throw NeverError(chainLayer);
    }, [chainLayer]);

    return (
      <Box
        position="relative"
        rounded="12px"
        {...props}
        {...styles.cardContainer}
      >
        <Box {...styles.cardBody}>
          <Flex flexDir="column" py="12px">
            <Box
              // Anchor point for scroll position restoration when navigating back from other page to agent list
              id={String(agent.id)}
              position="absolute"
              left="0"
              bottom="200%"
              pointerEvents="none"
            />
            <LinkOverlay asChild>
              <NextLinkWithIntl
                href={paths.agent(
                  agent.agent_token_address ?? agent.bonding_token_address,
                  chainLayer,
                )}
              />
            </LinkOverlay>
            <AgentCardAvatar
              variant={variant}
              agent={agent}
              position="absolute"
              top="-16px"
              left="-2px"
              link={paths.agent(
                agent.agent_token_address ?? agent.bonding_token_address,
                chainLayer,
              )}
            />
            <InDesktop>
              <Box px="16px">{createTextNode}</Box>
            </InDesktop>
            <Flex
              flexDir={{
                base: 'column',
                desktop: 'row',
              }}
            >
              <Flex flex="1" flexDir="column" gap="8px" py="12px" px="16px">
                <InMobile>{createTextNode}</InMobile>
                <AgentCardRow
                  title={
                    <AgentQueryHighlight>{agent.name}</AgentQueryHighlight>
                  }
                  content={
                    <HStack>
                      <Text
                        maxW="20ch"
                        lineClamp={1}
                        flexShrink="1"
                        {...styles.ticker}
                      >
                        <AgentQueryHighlight>
                          {`$${agent.symbol}`}
                        </AgentQueryHighlight>
                      </Text>
                      {checkHoursPassed({
                        date: agent.created_at,
                        hours: 24,
                      }) && (
                        <PercentChange
                          flexShrink="0"
                          value={agent.usd_price_change_1d}
                          textStyle="body3Bold"
                          whiteSpace="nowrap"
                        />
                      )}
                    </HStack>
                  }
                />
                <AgentCardRow
                  title="Market cap"
                  content={<UsdValue value={agent.market_cap} price="1" />}
                />
                {agent.is_graduated && (
                  <AgentCardRow
                    title={
                      <HStack color="state.success">
                        <Text>Graduated! </Text>
                        <MobileSupportTooltip content="This agent's fan club is finally big enough for them to graduate, so they're now a trade-able token.">
                          <MaskIcon
                            icon="url(/masks/info.svg)"
                            boxSize="16px"
                          />
                        </MobileSupportTooltip>
                      </HStack>
                    }
                    content={
                      <Link
                        color="primary"
                        href={EXTERNAL_LINK.swapAgentToken(
                          chainLayer,
                          agent.agent_token_address,
                        )}
                        target="_blank"
                        position="relative"
                      >
                        {tradeOnPlatformStr}
                        <MaskIcon
                          icon="url(/masks/arrow-outward.svg)"
                          boxSize="16px"
                        />
                      </Link>
                    }
                  />
                )}
                {!agent.is_graduated && (
                  <Box>
                    <AgentCardRow
                      title={
                        <HStack>
                          <Text>Graduate progress</Text>
                          <MobileSupportTooltip content="This agent needs the community to buy into its token before graduating into a trade-able token.">
                            <MaskIcon
                              icon="url(/masks/info.svg)"
                              boxSize="16px"
                              bg="currentcolor"
                            />
                          </MobileSupportTooltip>
                        </HStack>
                      }
                      content={
                        <Num
                          value={agent.graduate_progress.mul(100)}
                          suffix="/100%"
                          gap="0"
                        />
                      }
                    />
                    <Progress.Root
                      value={Number(
                        agent.graduate_progress.mul(100).toFixed(2),
                      )}
                      mt="8px"
                    >
                      <Progress.Track>
                        <Progress.Range />
                      </Progress.Track>
                    </Progress.Root>
                  </Box>
                )}
              </Flex>
              <Box {...styles.divider} />

              <Box
                flex="1"
                minH={{
                  base: '36px',
                  desktop: '72px',
                }}
                py="12px"
                px="16px"
              >
                <Text
                  wordBreak="break-word"
                  lineClamp={{
                    base: 2,
                    desktop: 4,
                  }}
                  textStyle="body3"
                >
                  <AgentQueryHighlight>{agent.description}</AgentQueryHighlight>
                </Text>
              </Box>
            </Flex>
          </Flex>
        </Box>
      </Box>
    );
  },
  (prev, next) => prev.agent.equals(next.agent),
);

AgentCard.displayName = 'AgentCardMemo';

const AgentQueryHighlight = ({ children }: { children: string }) => {
  const search = useAgentsView((s) => s.search);
  return (
    <Highlight query={search} ignoreCase>
      {children}
    </Highlight>
  );
};

interface AgentCardRowProps {
  title: ReactNode;
  content: ReactNode;
}
export const AgentCardRow = ({ title, content }: AgentCardRowProps) => {
  return (
    <HStack justifyContent="space-between">
      <Box textStyle="body2" flexShrink="0">
        {title}
      </Box>
      <Box
        textStyle="body2Bold"
        lineClamp={1}
        wordBreak="break-all"
        flexShrink="1"
      >
        {content}
      </Box>
    </HStack>
  );
};

type AgentCardAvatarProps = HTMLChakraProps<'div'> & {
  agent: Agent;
  variant?: 'special' | 'outline';
  link?: IntlInternalUrl;
};

export const AgentCardAvatar: React.FC<AgentCardAvatarProps> = ({
  agent,
  variant,
  link,
  ...props
}) => {
  const chainLayer = useGlobalChainLayer();

  const renderBling = () => {
    return [
      { left: '0px', top: '1px', boxSize: '16px' },
      { right: '-22px', top: '12px', boxSize: '20px' },
      { right: '-9px', bottom: '0px', boxSize: '14px' },
    ].map((style, key) => (
      <MaskIcon
        key={key}
        color="#138DFF"
        icon="url(/masks/bling.svg)"
        position="absolute"
        {...style}
      />
    ));
  };

  return (
    <Avatar src={agent.image_url} variant={variant} {...props}>
      {agent.is_graduated && (
        <Float placement="top-end" offsetX="2" offsetY="2">
          <Image
            src={DEX_LOGO[chainLayer]}
            alt={DEX_NAME_SHORT[chainLayer]}
            boxSize="16px"
          />
        </Float>
      )}
      {link && (
        <LinkOverlay asChild>
          <NextLinkWithIntl href={link} />
        </LinkOverlay>
      )}
      {variant === 'special' && renderBling()}
    </Avatar>
  );
};

const DEX_LOGO: Record<ChainLayer, string> = {
  [ChainLayer.CronosEvm]: '/tokens/vvs.svg',
  [ChainLayer.L2]: '/images/h2-logo.png',
};
